import { TransactionColumnToSort } from "@/modules/finances/utils/financeUtils"
import { ColumnSortingOrder } from "@/modules/types/datatableTypes"
import { LoggerService } from "@modules/common/logger/loggerService"
import { ApiClient } from "@modules/core/api/client/apiClient"
import { OutOfBandTransactionStatus } from "@modules/finances/boxPaymentAtCounter/entities/outOfBandTransactionType"
import {
  type OutOfBandTransactionDetailDto,
  OutOfBandTransactionDetailDtoSchema,
} from "@modules/finances/boxPaymentAtCounter/gateway/outOfBandTransactionDto"
import { type BoxFinanceApiGateway } from "@modules/finances/gateway/boxFinanceApiGateway"
import {
  type CustomerHistoryDto,
  CustomerHistoryDtoSchema,
  type OutOfBandCustomerHistoryDto,
  OutOfBandCustomerHistoryDtoSchema,
} from "@modules/finances/gateway/customerHistoryDto"
import { type FinanceStatisticsDto, FinanceStatisticsDtoSchema } from "@modules/finances/gateway/financeStatisticsDto"
import { TaxSettingsStatus } from "@modules/finances/gateway/taxesDto"
import {
  type TransactionDetailsDto,
  TransactionDetailsDtoSchema,
  type TransactionDto,
  TransactionDtoSchema,
} from "@modules/finances/gateway/transactionDto"
import { PaymentPlace, TransactionFilterStatus } from "@modules/finances/types/financesType"
import { type StripeErrorsDto } from "@modules/finances/types/stripeErrorTypes"
import { type Page, type Paginated, type PaginatedCursor, toPage } from "@modules/utils/paginated"

export class BoxFinanceApi implements BoxFinanceApiGateway {
  private boxFinanceBaseUrl = "/box/financial"
  private boxStripeErrorsUrl = "/stripe/box/errors"
  private boxFinanceOutOfBandBaseUrl = "/box/financial/out-of-band"
  private newBoxFinanceOutOfBandBaseUrl = "/box/financial/new/out-of-band"

  constructor(private api: ApiClient) {}

  async getTransactions(
    filter: TransactionFilterStatus | undefined,
    paymentPlace: PaymentPlace | undefined,
    nameFilter: string | undefined,
    period: string,
    page: number,
    size = 10,
    sortBy: TransactionColumnToSort = TransactionColumnToSort.DATE,
    sortOrder: ColumnSortingOrder = ColumnSortingOrder.DESC,
  ) {
    const result = await this.api.get<Paginated<TransactionDto>>(`${this.boxFinanceBaseUrl}/transactions`, {
      params: {
        page,
        size,
        period,
        status: filter,
        paymentMethodFilter: paymentPlace,
        search: nameFilter,
        sort: sortBy.split(",").map((s) => `${s},${sortOrder}`),
      },
      paramsSerializer: {
        indexes: null,
      },
    })

    const pageData = toPage(result.data)

    return {
      ...pageData,
      content: pageData.content.map((transaction) => LoggerService.parseApiDto(TransactionDtoSchema, transaction)),
    }
  }
  async getTransactionsHistory(stripeCustomerId: string, page?: string, size = 10) {
    const result = await this.api.get<PaginatedCursor<CustomerHistoryDto>>(
      `${this.boxFinanceBaseUrl}/customer/${stripeCustomerId}/history`,
      {
        params: {
          page,
          pageSize: size,
        },
      },
    )
    const pageData = result.data

    return {
      ...pageData,
      data: pageData.data.map((transaction) => LoggerService.parseApiDto(CustomerHistoryDtoSchema, transaction)),
    }
  }

  async getTransaction(transactionId: string): Promise<TransactionDetailsDto> {
    const result = await this.api.get<TransactionDetailsDto>(`${this.boxFinanceBaseUrl}/transactions/${transactionId}`)
    return LoggerService.parseApiDto(TransactionDetailsDtoSchema, result.data)
  }

  async refundTransaction(transactionId: string): Promise<void> {
    await this.api.post(`${this.boxFinanceBaseUrl}/transactions/${transactionId}/refund`)
  }

  async getStats(period: string): Promise<FinanceStatisticsDto> {
    const result = await this.api.get<FinanceStatisticsDto>(`${this.boxFinanceBaseUrl}/transactions/overview`, {
      params: {
        period,
      },
    })
    return LoggerService.parseApiDto(FinanceStatisticsDtoSchema, result.data)
  }

  async exportTransactions(startDate: string, endDate: string): Promise<string> {
    const result = await this.api.post<string>(
      `${this.boxFinanceBaseUrl}/export-transactions?startDate=${startDate}&endDate=${endDate}`,
    )
    return result.data
  }

  async exportMonthlyInvoices(yearMonth: string): Promise<{ blob: Blob; filename: string | undefined }> {
    const result = await this.api.post<Blob>(
      `${this.boxFinanceBaseUrl}/export-transactions/zip?yearMonth=${yearMonth}`,
      undefined,
      {
        responseType: "blob",
        headers: {
          Accept: "application/octet-stream",
        },
      },
    )

    const disposition = result.headers["content-disposition"] as string | undefined
    const filename = disposition?.match(/filename="?([^"]+)"?/)?.[1]

    return {
      blob: result.data,
      filename,
    }
  }

  async exportPayoutTransactions(payoutId: string): Promise<string> {
    const result = await this.api.get<string>(`${this.boxFinanceBaseUrl}/payouts/${payoutId}/export`)
    return result.data
  }

  async getStripeAccountErrors() {
    const result = await this.api.get<StripeErrorsDto | null>(`${this.boxStripeErrorsUrl}`)
    return result.status === 204 ? null : result.data
  }

  async validateOutOfBandTransaction(
    invoiceId: string,
    newStatus: OutOfBandTransactionStatus,
  ): Promise<OutOfBandTransactionDetailDto> {
    const result = await this.api.post<OutOfBandTransactionDetailDto>(
      `${this.boxFinanceOutOfBandBaseUrl}/${invoiceId}/validate?newStatus=${newStatus}`,
    )

    return LoggerService.parseApiDto(OutOfBandTransactionDetailDtoSchema, result.data)
  }

  async getOutOfBandCustomerHistory(
    stripeCustomerId: string,
    page: number,
    size = 20,
  ): Promise<Page<OutOfBandCustomerHistoryDto>> {
    const result = await this.api.get<Paginated<OutOfBandCustomerHistoryDto>>(
      `${this.newBoxFinanceOutOfBandBaseUrl}/customer/${stripeCustomerId}/history`,
      {
        params: { page, size, sort: "date,DESC" },
      },
    )

    const pageData = toPage(result.data)

    return {
      ...pageData,
      content: pageData.content.map((transaction) =>
        LoggerService.parseApiDto(OutOfBandCustomerHistoryDtoSchema, transaction),
      ),
    }
  }

  async activateTaxes(): Promise<TaxSettingsStatus> {
    const result = await this.api.post<TaxSettingsStatus>(`${this.boxFinanceBaseUrl}/taxes/activate`)
    return result.data
  }

  async getTaxesRegistrationToken(): Promise<string> {
    const result = await this.api.get<string>(`${this.boxFinanceBaseUrl}/taxes/registration`)
    return result.data
  }

  async getTransactionPdf(transactionId: string): Promise<Blob> {
    const result = await this.api.get<Blob>(`${this.boxFinanceBaseUrl}/transactions/${transactionId}/pdf`, {
      responseType: "blob",
    })
    return result.data
  }

  async getOutofBandTransactionPdf(invoiceId: string): Promise<Blob> {
    const result = await this.api.get<Blob>(`${this.boxFinanceBaseUrl}/out-of-band/${invoiceId}/pdf`, {
      responseType: "blob",
    })
    return result.data
  }
}
